import { EnumSelect } from '@core/components';
import { useKeyCombinationTrigger } from '@core/logic';
import { scanCategoryApiV1 } from '@deepup/apis';
import { Checkbox, FormControlLabel, Stack } from '@mui/material';
import { viewer3dActions, viewer3dSelectors } from '@viewer3D/redux';
import type { VisibleScans } from '@viewer3D/types';
import { useFlags } from '@flagsmith/flagsmith/react';
import { useDispatch, useSelector } from 'react-redux';
import { scanCategories, ScanCategories, useScanFilters } from '../hooks';

const getNextVisibleScansMode = (current: VisibleScans): VisibleScans => {
  if (current === 'ALL') return 'ONLY_VISIBLE';
  if (current === 'ONLY_VISIBLE') return 'NONE';
  return 'ALL';
};

export const ScansToggle = () => {
  const dispatch = useDispatch();
  const visibleScans = useSelector(viewer3dSelectors.visibleScans);
  const { scanCategory, setScanCategory, removeScanCategory } = useScanFilters();
  const { scan_category_filter_enabled } = useFlags(['scan_category_filter_enabled']);

  useKeyCombinationTrigger('Alt', 's', () => {
    dispatch(viewer3dActions.setVisibleScans(getNextVisibleScansMode(visibleScans)));
  });

  const handleChange = (value: ScanCategories) => {
    if (value.length === 0) {
      removeScanCategory();
    } else {
      const category = scanCategories.find((c) => c.name === value);
      if (category) {
        setScanCategory(category.id);
      }
    }
  };

  const getCurrentCategory = () => {
    const currentCategory = scanCategories.find((c) => c.id === scanCategory);

    if (!currentCategory) {
      return { id: scanCategoryApiV1.ScanCategoryKind.UNSPECIFIED, name: 'Unspecified' };
    }

    return currentCategory;
  };

  const currentCategory = getCurrentCategory().name;

  const hasScansEnabled = visibleScans === 'ALL' || visibleScans === 'ONLY_VISIBLE';

  return (
    <Stack direction="row">
      <FormControlLabel
        label="Scans"
        control={
          <Checkbox
            color="primary"
            checked={visibleScans === 'ALL' || visibleScans === 'ONLY_VISIBLE'}
            indeterminate={visibleScans === 'ONLY_VISIBLE'}
            onChange={() =>
              dispatch(viewer3dActions.setVisibleScans(getNextVisibleScansMode(visibleScans)))
            }
          />
        }
      />
      {scan_category_filter_enabled.enabled && (
        <EnumSelect
          label="Scan category"
          value={currentCategory}
          onChange={(event) => handleChange(event.target.value as ScanCategories)}
          options={scanCategories.map((c) => c.name)}
          disabled={!hasScansEnabled}
        />
      )}
    </Stack>
  );
};
